/**
 * Nova DateTime Helper
 * Centralized utility for formatting dates with timezone support
 * Handles presale date formatting and tooltip initialization
 */

/**
 * Formats an ISO date string to a localized date string with timezone conversion
 * @param {string} isoString - ISO 8601 date string (e.g., "2026-01-15T20:00:00Z")
 * @param {Object} [options] - Formatting options
 * @param {string} [options.timeZone="America/Toronto"] - IANA timezone identifier
 * @param {string} [options.locale="fr-CA"] - BCP 47 locale identifier
 * @returns {string} Formatted date string (e.g., "15 janvier 2026 à 20 h 00")
 */
function formatZonedDate(isoString, options = {}) {
  if (!isoString || typeof isoString !== "string") {
    return "";
  }

  const {timeZone = "America/Toronto", locale = "fr-CA"} = options;

  try {
    const date = new Date(isoString);

    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string: ${isoString}`);
      return "";
    }

    // Format: "D mois AAAA à HH h mm"
    const formatter = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: timeZone,
    });

    const parts = formatter.formatToParts(date);

    // Extract parts
    const day = parts.find(p => p.type === "day")?.value || "";
    const month = parts.find(p => p.type === "month")?.value || "";
    const year = parts.find(p => p.type === "year")?.value || "";
    const hour = parts.find(p => p.type === "hour")?.value || "";
    const minute = parts.find(p => p.type === "minute")?.value || "";

    // Format: "15 janvier 2026 à 20 h 00"
    return `${day} ${month} ${year} à ${hour} h ${minute}`;
  } catch (error) {
    console.error(`Error formatting date: ${isoString}`, error);
    return "";
  }
}

/**
 * Initializes presale tooltips by finding elements with data-presale-end attribute
 * and injecting formatted dates into their target elements
 * @param {HTMLElement|Document} [root=document] - Root element to search within
 */
function initPresaleTooltips(root = document) {
  // Find all elements with data-presale-end attribute that haven't been initialized
  const presaleElements = root.querySelectorAll('[data-presale-end]:not([data-presale-initialized="true"])');

  presaleElements.forEach(element => {
    const isoDate = element.getAttribute("data-presale-end");

    if (!isoDate || isoDate.trim() === "") {
      return;
    }

    // Format the date
    const formattedDate = formatZonedDate(isoDate);

    if (!formattedDate) {
      console.warn(`Could not format date: ${isoDate}`);
      return;
    }

    // Find the target element for the formatted date
    // The element with data-presale-end is typically inside .tooltip-text
    const tooltipText = element.closest(".tooltip-text");

    if (tooltipText) {
      // Get the "on_date" translation prefix from .tooltip-text
      const onDateText = tooltipText.getAttribute("data-on-date-text") || "Le";

      // Replace the entire content of .tooltip-text with formatted date
      tooltipText.textContent = `${onDateText} ${formattedDate}`;
    } else {
      // Fallback: if no .tooltip-text found, look for data-presale-end-output
      let targetElement =
        element.querySelector("[data-presale-end-output]") ||
        element.parentElement?.querySelector("[data-presale-end-output]");

      if (targetElement) {
        const onDateText =
          targetElement.getAttribute("data-on-date-text") ||
          targetElement.closest(".tooltip-text")?.getAttribute("data-on-date-text") ||
          "Le";
        targetElement.textContent = `${onDateText} ${formattedDate}`;
      } else {
        // Last resort: update the element itself
        const onDateText = element.getAttribute("data-on-date-text") || "Le";
        element.textContent = `${onDateText} ${formattedDate}`;
      }
    }

    // Mark as initialized to prevent re-processing
    element.setAttribute("data-presale-initialized", "true");
  });
}

// Make functions available globally for non-module scripts
if (typeof window !== "undefined") {
  window.NovaDateTime = {
    formatZonedDate,
    initPresaleTooltips,
  };
}

// Auto-initialize on DOMContentLoaded if in browser environment
if (typeof document !== "undefined" && document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    initPresaleTooltips();
  });
} else if (typeof document !== "undefined") {
  // DOM already loaded
  initPresaleTooltips();
}
